import { useState } from "react";
import { Radio, LogIn, PlayCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { IDS } from "../constants/testIds";

export default function LoginPage() {
  const { login, loginDemo } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const demo = async () => {
    setBusy(true); setError("");
    try { await loginDemo(); }
    catch (err) { setError(formatApiError(err, "Demo unavailable")); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(email.trim(), password); }
    catch (err) { setError(formatApiError(err, "Login failed")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={IDS.login.page} className="min-h-screen grid-bg grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-slate-900 text-white grid place-items-center shadow-md"><Radio className="h-6 w-6" /></div>
          <div>
            <div className="overline">Zello · Dispatch Console</div>
            <h1 className="text-2xl font-semibold tracking-tight">Dispatcher sign-in</h1>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm grid gap-4">
          <div className="grid gap-1.5">
            <Label>Email</Label>
            <Input data-testid={IDS.login.email} type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="dispatcher@agency.gov" required />
          </div>
          <div className="grid gap-1.5">
            <Label>Password</Label>
            <Input data-testid={IDS.login.password} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          {error && <p data-testid={IDS.login.error} className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
          <Button data-testid={IDS.login.submit} type="submit" disabled={busy} className="gap-2 h-11">
            <LogIn className="h-4 w-4" /> {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="relative text-center text-[11px] text-slate-400"><span className="bg-white px-2 relative z-10">or</span><span className="absolute inset-x-0 top-1/2 border-t border-slate-200" /></div>
          <Button data-testid={IDS.login.demo} type="button" variant="outline" disabled={busy} onClick={demo} className="gap-2 h-11 border-teal-300 text-teal-700 hover:bg-teal-50">
            <PlayCircle className="h-4 w-4" /> Try the live demo
          </Button>
          <p className="text-[11px] text-slate-500 text-center">Demo uses simulated field traffic — no credentials needed. Accounts are created by your console administrator.</p>
        </form>
      </div>
    </div>
  );
}
